import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  collection,
  query,
  orderBy,
  DocumentData,
  QueryDocumentSnapshot,
} from 'firebase/firestore';
import CreateMarketplaceRequestDialog from '../Marketplace/CreateMarketplaceRequestDialog';
import './SupportTicketDetailPage.css';

interface MessageAttachment {
  storagePath: string;
  fileName: string;
  contentType: string;
  size: number;
}

interface MessageTranslation {
  text: string;
  sourceLanguage: string;
  model?: string;
}

interface MessageReaction {
  uid: string;
  name: string;
}

interface Message {
  id: string;
  senderUid?: string;
  senderRole?: string;
  senderPhotoUrl?: string | null;
  text: string;
  timestamp: any;
  editedAt?: any;
  senderName?: string;
  attachments?: MessageAttachment[];
}

type MessageReactions = Record<string, MessageReaction[]>;

interface Ticket {
  id: string;
  subject: string;
  status: string;
  userName: string;
  userId: string;
  category?: string;
  description?: string;
  relatedCaseId?: string;
  assignedTo: string;
  assignedToName: string;
  statusChangedBy?: string;
}

interface CustomerCase {
  id: string;
  service?: string | null;
  status?: string | null;
  lawyer_status?: string | null;
  created_at?: any;
  completed_at?: any;
}

interface SupportUser {
  email: string;
  name: string;
}

interface InternalNote {
  id: string;
  text: string;
  authorName?: string;
  authorEmail?: string;
  mentions: string[];
  createdAt?: any;
}

type AvatarStatus = 'pending' | 'ready' | 'fallback';

interface AvatarState {
  status: AvatarStatus;
  url: string | null;
  label: string;
  fallbackInitial: string;
}

const DEFAULT_CUSTOMER_LABEL = 'Customer';

// A reply moves an Open or In progress case to Waiting on user by itself
// (COE-221). The customer gets the "New reply" email for it and nothing else,
// so the usual Waiting on user hint ("emails the customer") would be wrong.
const AUTO_WAITING_HINT =
  'Set by your reply. The customer got the reply email, nothing more. Goes back to Open when they answer.';

// Same slugs as SUPPORT_REACTIONS in the partner backend, which rejects any
// other. Order is the order of the picker.
const REACTIONS: { key: string; emoji: string; label: string }[] = [
  { key: 'thumbs_up', emoji: '👍', label: 'Thumbs up' },
  { key: 'heart', emoji: '❤️', label: 'Heart' },
  { key: 'laugh', emoji: '😂', label: 'Laugh' },
  { key: 'tada', emoji: '🎉', label: 'Celebrate' },
  { key: 'pray', emoji: '🙏', label: 'Thanks' },
  { key: 'eyes', emoji: '👀', label: 'Looking' },
  { key: 'check', emoji: '✅', label: 'Done' },
];

// Reactions are NOT on the message document: customers can read their own
// tickets' messages, and reactions are team-only. They come from
// GET /support-tickets/{id}/reactions, keyed by message id.
const normalizeReactions = (value: unknown): MessageReactions | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const result: MessageReactions = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, entries]) => {
    if (!Array.isArray(entries)) return;
    const valid = entries.filter(
      (entry: any): entry is MessageReaction => entry && typeof entry.uid === 'string'
    );
    if (valid.length > 0) result[key] = valid;
  });
  return Object.keys(result).length > 0 ? result : undefined;
};

// What each end state actually does, shown next to the picker. Support asked
// what the difference is; nothing in the product had ever said.
const STATUS_OPTIONS: { value: string; label: string; hint: string }[] = [
  {
    value: 'open',
    label: 'Open',
    hint: 'Waiting on us. No email is sent. Replying moves it to Waiting on user.',
  },
  { value: 'in_progress', label: 'In progress', hint: 'Being worked on. No email is sent.' },
  {
    value: 'waiting_on_user',
    label: 'Waiting on user',
    hint:
      'Picked here, it emails the customer that we need something. Pauses the waiting clock. Goes back to Open when they answer.',
  },
  {
    value: 'resolved',
    label: 'Resolved',
    hint: 'Emails the customer that the case is resolved. They can still reply, which reopens it.',
  },
  {
    value: 'closed',
    label: 'Closed',
    hint: 'Silent. Nothing is sent. Use it for duplicates and spam.',
  },
];

// The composer grows with the draft up to this share of the window, then
// scrolls. An agent can still drag it taller; that height is remembered.
const COMPOSER_MAX_VIEWPORT_SHARE = 0.5;
const COMPOSER_MIN_HEIGHT = 130;
// Minimized: one line of the draft, enough to see it is still there. A long
// reply otherwise fills the pane and pushes the customer's message, the thing
// the reply is answering, off the top of the screen.
const COMPOSER_COLLAPSED_HEIGHT = 56;
const COMPOSER_HEIGHT_STORAGE_KEY = 'coepi.support.composerHeight';

const getAvatarKey = (message: Message) =>
  message.senderUid || message.id;

const getInitialFromLabel = (label?: string) => {
  if (!label) {
    return '?';
  }
  const trimmed = label.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

// Some stored messages were HTML-escaped on the way into Firestore (e.g. an
// apostrophe persisted as "&#x27;"). React escapes text on render, so those
// entities would otherwise show up literally. Decode them once here so the
// message renders as the human-readable text the agent actually wrote. A single
// left-to-right pass means "&amp;lt;" correctly stays "&lt;" rather than
// double-decoding to "<".
const decodeHtmlEntities = (value: string): string =>
  value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, body: string) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X';
      const codePoint = isHex
        ? parseInt(body.slice(2), 16)
        : parseInt(body.slice(1), 10);
      if (Number.isNaN(codePoint)) {
        return match;
      }
      try {
        return String.fromCodePoint(codePoint);
      } catch {
        return match;
      }
    }
    const named = NAMED_HTML_ENTITIES[body.toLowerCase()];
    return named !== undefined ? named : match;
  });

const normalizeMessage = (docSnap: QueryDocumentSnapshot<DocumentData>): Message => {
  const data = docSnap.data() ?? {};
  const senderUid =
    typeof data.senderUid === 'string' && data.senderUid.trim()
      ? data.senderUid.trim()
      : undefined;
  const senderPhotoUrl =
    typeof data.senderPhotoUrl === 'string' && data.senderPhotoUrl.trim()
      ? data.senderPhotoUrl.trim()
      : undefined;

  return {
    id: docSnap.id,
    senderUid,
    senderPhotoUrl: senderPhotoUrl ?? null,
    senderName: typeof data.senderName === 'string' ? data.senderName : undefined,
    senderRole: typeof data.senderRole === 'string' ? data.senderRole : undefined,
    text: typeof data.text === 'string' ? decodeHtmlEntities(data.text) : '',
    timestamp: data.timestamp ?? null,
    editedAt: data.editedAt ?? undefined,
    attachments: Array.isArray(data.attachments)
      ? data.attachments.filter(
          (a: any): a is MessageAttachment =>
            a && typeof a.storagePath === 'string' && typeof a.fileName === 'string'
        )
      : undefined,
  };
};

const normalizeTranslation = (data: DocumentData | undefined): MessageTranslation | null => {
  if (!data || typeof data.text !== 'string' || !data.text.trim()) return null;
  return {
    text: data.text,
    sourceLanguage:
      typeof data.sourceLanguage === 'string' ? data.sourceLanguage : 'und',
    model: typeof data.model === 'string' ? data.model : undefined,
  };
};

const LANGUAGE_LABELS: Record<string, string> = {
  pt: 'Portuguese',
  es: 'Spanish',
  de: 'German',
  fr: 'French',
  uk: 'Ukrainian',
  ru: 'Russian',
  it: 'Italian',
  nl: 'Dutch',
  pl: 'Polish',
  ro: 'Romanian',
  tr: 'Turkish',
  ar: 'Arabic',
  zh: 'Chinese',
  hi: 'Hindi',
};

const languageLabel = (code: string): string =>
  LANGUAGE_LABELS[code] || (code && code !== 'und' ? code.toUpperCase() : 'another language');

const formatAttachmentSize = (size?: number): string => {
  if (size === undefined || Number.isNaN(size)) {
    return '';
  }
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

const toDateFromTimestamp = (value: any): Date | null => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  if (typeof value === 'object') {
    if (typeof value.toDate === 'function') {
      try {
        return value.toDate();
      } catch {
        return null;
      }
    }
    if (typeof value.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
  }
  return null;
};

const formatTimestamp = (value: any) => {
  const date = toDateFromTimestamp(value);
  return date ? date.toLocaleString() : '';
};

interface AvatarBubbleProps {
  cacheKey: string;
  avatarState?: AvatarState;
  fallbackInitial: string;
  alt: string;
  onLoad: (cacheKey: string) => void;
  onError: (cacheKey: string) => void;
}

const AvatarBubble: React.FC<AvatarBubbleProps> = ({
  cacheKey,
  avatarState,
  fallbackInitial,
  alt,
  onLoad,
  onError,
}) => {
  const avatarStatus = avatarState?.status ?? 'fallback';
  const avatarUrl = avatarState?.url ?? null;

  return (
    <div
      className={`avatar ${avatarUrl ? 'avatar-has-image' : ''} ${
        avatarStatus === 'pending' ? 'avatar-pending' : ''
      }`}
    >
      {avatarUrl && (
        <img
          src={avatarUrl}
          alt={alt}
          className={avatarStatus === 'ready' ? '' : 'avatar-image-loading'}
          onLoad={() => onLoad(cacheKey)}
          onError={() => onError(cacheKey)}
        />
      )}
      {avatarStatus === 'pending' && avatarUrl && (
        <span className="avatar-loading" aria-hidden="true" />
      )}
      {(!avatarUrl || avatarStatus === 'fallback') && (
        <span className="avatar-fallback" aria-hidden="true">
          {fallbackInitial}
        </span>
      )}
    </div>
  );
};

interface SupportTicketDetailPageProps {
  // Marketplace admins (in-house lawyers with marketplaceAdmin) can turn a
  // ticket into a marketplace request the classifier missed.
  canCreateMarketplaceRequest?: boolean;
}

const SupportTicketDetailPage: React.FC<SupportTicketDetailPageProps> = ({
  canCreateMarketplaceRequest = false,
}) => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const [ticketMeta, setTicketMeta] = useState<Omit<Ticket, 'messages'> | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [draftState, setDraftState] = useState<'idle' | 'generating' | 'present' | 'error'>('idle');
  const [draftSnapshot, setDraftSnapshot] = useState('');
  const [draftMeta, setDraftMeta] = useState<{
    draftId: string;
    language: string;
    grounded: boolean;
    sources: { type: string; title: string; messageCount?: number }[];
  } | null>(null);
  const [draftEdited, setDraftEdited] = useState(false);
  const [deliveredDraftText, setDeliveredDraftText] = useState('');
  const draftButtonRef = useRef<HTMLButtonElement>(null);
  const translationRequestedFor = useRef<Set<string>>(new Set());
  // Editing a reply that was already sent (COE-221). One at a time.
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  // Which message has the emoji picker open, and which have a reaction request
  // in flight (a double click would otherwise add and remove in one go).
  const [reactionPickerFor, setReactionPickerFor] = useState<string | null>(null);
  const reactionBusy = useRef<Set<string>>(new Set());
  const [reactionsByMessage, setReactionsByMessage] = useState<Record<string, MessageReactions>>({});
  // A failed reaction is shown on its own message. The page-level error
  // replaces the whole ticket view, far too much for a missed emoji.
  const [reactionError, setReactionError] = useState<{ messageId: string; text: string } | null>(null);

  // Close the emoji picker on a click anywhere else, or on Escape.
  useEffect(() => {
    if (!reactionPickerFor) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setReactionPickerFor(null);
        return;
      }
      const target = event.target as Element | null;
      if (!target?.closest?.('.reaction-picker-anchor')) setReactionPickerFor(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [reactionPickerFor]);
  const [translations, setTranslations] = useState<Record<string, MessageTranslation>>({});

  // Fire-and-forget lifecycle telemetry — must never block or fail the send path.
  const fireDraftEvent = (
    event: 'regenerated' | 'discarded' | 'sent',
    extras: { editDistanceRatio?: number } = {}
  ) => {
    if (!draftMeta) return;
    void (async () => {
      try {
        const token = await user?.getIdToken();
        if (!token) return;
        await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/draft-reply/events`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ draftId: draftMeta.draftId, event, ...extras }),
        });
      } catch {
        // telemetry is best-effort
      }
    })();
  };
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [avatarCache, setAvatarCache] = useState<Record<string, AvatarState>>({});
  const [customerCases, setCustomerCases] = useState<CustomerCase[]>([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMarketplaceDialogOpen, setIsMarketplaceDialogOpen] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [supportUsers, setSupportUsers] = useState<SupportUser[]>([]);

  // Internal notes: the agents' own margin on the case. Never sent, never seen
  // by the customer, and deliberately not part of the message thread.
  const [composerTab, setComposerTab] = useState<'reply' | 'note'>('reply');
  const [notes, setNotes] = useState<InternalNote[]>([]);
  const [noteText, setNoteText] = useState('');
  const [noteSending, setNoteSending] = useState(false);
  const [noteError, setNoteError] = useState('');
  const [mentioned, setMentioned] = useState<SupportUser[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);

  // What the draft says in English, when it was written in another language.
  const [draftEnglish, setDraftEnglish] = useState('');
  const [isEnglishOpen, setIsEnglishOpen] = useState(true);

  const replyRef = useRef<HTMLTextAreaElement>(null);
  const noteRef = useRef<HTMLTextAreaElement>(null);
  // Set once an agent drags the composer: their height wins over auto-grow.
  const draggedHeightRef = useRef<number | null>(null);
  // The last height autoGrow itself applied, so the drag watcher below can tell
  // our own writes apart from the agent pulling the resize handle.
  const autoSetHeightRef = useRef<number | null>(null);
  const [isComposerCollapsed, setIsComposerCollapsed] = useState(false);

  const ticket = useMemo(
    () => (ticketMeta ? { ...ticketMeta, messages } : null),
    [ticketMeta, messages]
  );

  const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8090';

  const handleAvatarLoad = useCallback((cacheKey: string) => {
    setAvatarCache((prev) => {
      const existing = prev[cacheKey];
      if (!existing || existing.status === 'ready') {
        return prev;
      }
      return {
        ...prev,
        [cacheKey]: {
          ...existing,
          status: 'ready',
        },
      };
    });
  }, []);

  const handleAvatarError = useCallback((cacheKey: string) => {
    if (process.env.NODE_ENV !== 'test') {
      // Surface avatar failures so we can attach monitoring later.
      // eslint-disable-next-line no-console
      console.warn('Chat avatar failed to load', { cacheKey });
    }
    setAvatarCache((prev) => {
      const existing = prev[cacheKey];
      if (!existing || existing.status === 'fallback') {
        return prev;
      }
      return {
        ...prev,
        [cacheKey]: {
          ...existing,
          status: 'fallback',
          url: null,
        },
      };
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !ticketId) {
      setTicketMeta(null);
      setMessages([]);
      setAvatarCache({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const ticketRef = doc(db, 'support_tickets', ticketId);
    const messagesQueryRef = query(
      collection(ticketRef, 'messages'),
      orderBy('timestamp')
    );

    const unsubscribeTicket = onSnapshot(
      ticketRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() ?? {};
          const nextMeta: Omit<Ticket, 'messages'> = {
            id: docSnap.id,
            subject:
              typeof data.subject === 'string' ? data.subject : 'Support Ticket',
            status: typeof data.status === 'string' ? data.status : 'open',
            userName:
              typeof data.userName === 'string'
                ? data.userName
                : DEFAULT_CUSTOMER_LABEL,
            userId: typeof data.userId === 'string' ? data.userId : '',
            category: typeof data.category === 'string' ? data.category : 'other',
            description: typeof data.description === 'string' ? data.description : '',
            relatedCaseId:
              typeof data.relatedCaseId === 'string' ? data.relatedCaseId : undefined,
            assignedTo:
              typeof data.assignedTo === 'string' ? data.assignedTo : '',
            assignedToName:
              typeof data.assignedToName === 'string' ? data.assignedToName : '',
            statusChangedBy:
              typeof data.statusChangedBy === 'string' ? data.statusChangedBy : undefined,
          };
          setTicketMeta(nextMeta);
          setIsLoading(false);
          setError('');
        } else {
          setTicketMeta(null);
          setMessages([]);
          setError('Ticket not found.');
          setIsLoading(false);
        }
      },
      () => {
        setError('Failed to load ticket.');
        setIsLoading(false);
      }
    );

    const unsubscribeMessages = onSnapshot(
      messagesQueryRef,
      (snapshot) => {
        const nextMessages = snapshot.docs.map(normalizeMessage);
        setMessages(nextMessages);
      },
      () => {
        setError('Failed to load messages.');
      }
    );

    // Translations live in their own lawyers-only subcollection, keyed by
    // message id. A failure here is silent: the originals still render.
    const unsubscribeTranslations = onSnapshot(
      collection(ticketRef, 'translations'),
      (snapshot) => {
        const next: Record<string, MessageTranslation> = {};
        snapshot.docs.forEach((docSnap) => {
          const parsed = normalizeTranslation(docSnap.data());
          if (parsed) next[docSnap.id] = parsed;
        });
        setTranslations(next);
      },
      () => {}
    );

    return () => {
      unsubscribeTicket();
      unsubscribeMessages();
      unsubscribeTranslations();
    };
  }, [user, ticketId]);

  // Ask the backend to translate any message that has no cached translation
  // yet, whoever wrote it. The results land in the translations subcollection,
  // so the snapshot listener above delivers them; nothing is merged into state
  // here.
  //
  // Agent replies are included: an agent who speaks the customer's language
  // answers in it, and the next agent to open the case needs that half of the
  // thread as much as the customer's half.
  //
  // Tracked per message id rather than per ticket: a reply arriving while the
  // agent has the case open must get translated too, but a message we already
  // asked about must never be asked about again, or a message the model keeps
  // failing on would spin the endpoint forever.
  useEffect(() => {
    if (!user || !ticketId) return;

    const pending = messages
      .filter(
        (message) =>
          message.id &&
          message.text.trim() &&
          !translations[message.id] &&
          !translationRequestedFor.current.has(message.id)
      )
      .map((message) => message.id);
    if (!pending.length) return;

    pending.forEach((id) => translationRequestedFor.current.add(id));
    void (async () => {
      try {
        const token = await user.getIdToken();
        if (!token) return;
        await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/translate`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Translation is an assist, never a blocker: the original text is
        // already on screen either way.
      }
    })();
  }, [user, ticketId, messages, translations, BACKEND_URL]);

  useEffect(() => {
    if (!messages.length) {
      setAvatarCache((prev) => (Object.keys(prev).length ? {} : prev));
      return;
    }

    setAvatarCache((prev) => {
      let next = prev;
      let mutated = false;
      const seenKeys = new Set<string>();

      messages.forEach((message) => {
        const cacheKey = getAvatarKey(message);
        seenKeys.add(cacheKey);

        const isAgentMessage =
          Boolean(user?.uid) &&
          message.senderUid === user?.uid;

        const defaultLabel = isAgentMessage
          ? user?.displayName || 'You'
          : message.senderName || ticketMeta?.userName || DEFAULT_CUSTOMER_LABEL;
        const fallbackInitial = getInitialFromLabel(defaultLabel);

        const normalizedPhoto =
          message.senderPhotoUrl && message.senderPhotoUrl.trim()
            ? message.senderPhotoUrl.trim()
            : undefined;
        const candidateUrl = (() => {
          if (isAgentMessage && user?.photoURL && user.photoURL.trim()) {
            return user.photoURL.trim();
          }
          return normalizedPhoto;
        })();

        const existing = next[cacheKey];
        const desiredUrl = candidateUrl ?? null;
        const desiredStatus: AvatarStatus = candidateUrl
          ? existing?.status === 'ready' && existing.url === desiredUrl
            ? 'ready'
            : 'pending'
          : 'fallback';

        if (
          !existing ||
          existing.url !== desiredUrl ||
          existing.label !== defaultLabel ||
          existing.fallbackInitial !== fallbackInitial ||
          existing.status !== desiredStatus
        ) {
          if (!mutated) {
            next = { ...next };
            mutated = true;
          }
          next[cacheKey] = {
            url: desiredUrl,
            label: defaultLabel,
            fallbackInitial,
            status: desiredStatus,
          };
        }
      });

      Object.keys(next).forEach((cacheKey) => {
        if (!seenKeys.has(cacheKey)) {
          if (!mutated) {
            next = { ...next };
            mutated = true;
          }
          delete next[cacheKey];
        }
      });

      return mutated ? next : prev;
    });
  }, [messages, user, ticketMeta?.userName]);

  useEffect(() => {
    setAvatarCache({});
  }, [ticketId, user?.uid]);

  // Close the profile drawer when switching tickets.
  useEffect(() => {
    setIsProfileOpen(false);
  }, [ticketId]);

  // Allow closing the profile drawer with the Escape key.
  useEffect(() => {
    if (!isProfileOpen) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsProfileOpen(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isProfileOpen]);

  useEffect(() => {
    if (!user || !ticketId) {
      setCustomerCases([]);
      return;
    }

    let cancelled = false;
    setCasesLoading(true);

    (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${BACKEND_URL}/support-tickets/${ticketId}/cases`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const data = await response.json();
        if (!cancelled) {
          setCustomerCases(Array.isArray(data.cases) ? data.cases : []);
        }
      } catch (err) {
        if (!cancelled) {
          setCustomerCases([]);
        }
      } finally {
        if (!cancelled) {
          setCasesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, ticketId, BACKEND_URL]);

  // Grow the composer with its content instead of making the agent read a long
  // draft through a four-line slot. An agent who drags the handle themselves
  // owns the height from then on, and it survives reloads.
  const applyHeight = (element: HTMLTextAreaElement, height: number) => {
    autoSetHeightRef.current = height;
    element.style.height = `${height}px`;
  };

  const autoGrow = useCallback(
    (element: HTMLTextAreaElement | null) => {
      if (!element) return;
      if (isComposerCollapsed) {
        applyHeight(element, COMPOSER_COLLAPSED_HEIGHT);
        return;
      }
      if (draggedHeightRef.current !== null) {
        applyHeight(element, draggedHeightRef.current);
        return;
      }
      const max = Math.max(
        COMPOSER_MIN_HEIGHT,
        Math.round(window.innerHeight * COMPOSER_MAX_VIEWPORT_SHARE)
      );
      element.style.height = 'auto';
      applyHeight(
        element,
        Math.min(Math.max(element.scrollHeight, COMPOSER_MIN_HEIGHT), max)
      );
    },
    [isComposerCollapsed]
  );

  // Restore a height the agent dragged in an earlier session.
  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(COMPOSER_HEIGHT_STORAGE_KEY);
      const parsed = stored ? parseInt(stored, 10) : NaN;
      if (!Number.isNaN(parsed) && parsed >= COMPOSER_MIN_HEIGHT) {
        draggedHeightRef.current = parsed;
      }
    } catch {
      // Private mode or blocked storage: auto-grow is a fine default.
    }
  }, []);

  const activeComposerRef = composerTab === 'reply' ? replyRef : noteRef;

  useEffect(() => {
    autoGrow(activeComposerRef.current);
  }, [newMessage, noteText, composerTab, autoGrow, activeComposerRef]);

  // Watch for a manual drag on the composer and remember that height.
  useEffect(() => {
    const element = activeComposerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    let last = element.clientHeight;
    const observer = new ResizeObserver(() => {
      const height = element.clientHeight;
      if (Math.abs(height - last) < 2) return;
      last = height;
      // Only a height we did NOT set is a drag. The previous check compared
      // the height against the content, which is exactly what autoGrow makes
      // them equal, so every keystroke that grew the box was recorded as a
      // deliberate drag and persisted. The composer then reopened at that
      // height with auto-grow disabled, and only ever got taller.
      if (
        autoSetHeightRef.current !== null &&
        Math.abs(height - autoSetHeightRef.current) < 2
      ) {
        return;
      }
      // A collapse is ours too, and must not become the remembered height.
      if (isComposerCollapsed) return;
      draggedHeightRef.current = height;
      try {
        window.localStorage.setItem(COMPOSER_HEIGHT_STORAGE_KEY, String(height));
      } catch {
        // Nothing to do: the height still applies for this session.
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [activeComposerRef, composerTab, isComposerCollapsed]);

  // The agents a ticket can be assigned to, and who can be @mentioned.
  useEffect(() => {
    if (!user) {
      setSupportUsers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(`${BACKEND_URL}/support-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const data = await response.json();
        if (!cancelled) setSupportUsers(Array.isArray(data.users) ? data.users : []);
      } catch {
        // Without the list the inbox degrades to self-assign, which is what it
        // could always do. Not worth an error banner.
        if (!cancelled) setSupportUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, BACKEND_URL]);

  const loadNotes = useCallback(async () => {
    if (!user || !ticketId) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/notes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const data = await response.json();
      setNotes(
        (Array.isArray(data.notes) ? data.notes : []).map((note: any) => ({
          id: note.id,
          text: decodeHtmlEntities(typeof note.text === 'string' ? note.text : ''),
          authorName: note.authorName,
          authorEmail: note.authorEmail,
          mentions: Array.isArray(note.mentions) ? note.mentions : [],
          createdAt: note.createdAt,
        }))
      );
    } catch {
      setNoteError('Could not load the internal notes.');
    }
  }, [user, ticketId, BACKEND_URL]);

  useEffect(() => {
    setNotes([]);
    setNoteText('');
    setMentioned([]);
    setComposerTab('reply');
    setIsComposerCollapsed(false);
    loadNotes();
  }, [ticketId, loadNotes]);

  // --- @mentions -------------------------------------------------------------
  // The token being typed after an "@", or null when the cursor is not in one.
  const mentionMatches = useMemo(() => {
    if (mentionQuery === null) return [];
    const needle = mentionQuery.toLowerCase();
    return supportUsers
      .filter(
        (candidate) =>
          candidate.email.toLowerCase() !== (user?.email ?? '').toLowerCase() &&
          (candidate.name.toLowerCase().includes(needle) ||
            candidate.email.toLowerCase().includes(needle))
      )
      .slice(0, 6);
  }, [mentionQuery, supportUsers, user]);

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    setNoteText(value);
    setNoteError('');

    const caret = event.target.selectionStart ?? value.length;
    const upToCaret = value.slice(0, caret);
    // An "@" only opens the picker at a word boundary, so an email address
    // typed into a note does not turn into a mention menu.
    const match = /(^|\s)@([\w.-]*)$/.exec(upToCaret);
    setMentionQuery(match ? match[2] : null);
    setMentionIndex(0);
  };

  const insertMention = (candidate: SupportUser) => {
    const element = noteRef.current;
    const caret = element?.selectionStart ?? noteText.length;
    const upToCaret = noteText.slice(0, caret);
    const replaced = upToCaret.replace(/(^|\s)@([\w.-]*)$/, `$1@${candidate.name} `);
    const next = replaced + noteText.slice(caret);

    setNoteText(next);
    setMentioned((prev) =>
      prev.some((m) => m.email === candidate.email) ? prev : [...prev, candidate]
    );
    setMentionQuery(null);
    window.requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(replaced.length, replaced.length);
    });
  };

  const handleNoteKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery === null || mentionMatches.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionIndex((i) => (i + 1) % mentionMatches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionIndex((i) => (i - 1 + mentionMatches.length) % mentionMatches.length);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      insertMention(mentionMatches[mentionIndex]);
    } else if (event.key === 'Escape') {
      setMentionQuery(null);
    }
  };

  const submitNote = async () => {
    const text = noteText.trim();
    if (!text || noteSending || !user || !ticketId) return;

    setNoteSending(true);
    setNoteError('');
    try {
      const token = await user.getIdToken();
      // Only names still present in the text count as mentions: deleting the
      // name out of the note must not leave someone on the email list.
      const mentions = mentioned
        .filter((candidate) => text.includes(`@${candidate.name}`))
        .map((candidate) => candidate.email);

      const response = await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/notes`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text, mentions }),
      });
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      setNoteText('');
      setMentioned([]);
      setMentionQuery(null);
      await loadNotes();
    } catch {
      setNoteError('The note could not be saved.');
    } finally {
      setNoteSending(false);
    }
  };

  const submitMessage = async () => {
    // A reply is valid when it has text OR at least one attachment.
    if (!newMessage.trim() && stagedFiles.length === 0) return;
    if (isSending) return;

    try {
      setIsSending(true);
      const token = await user?.getIdToken();
      if (!token) {
        setError('Authentication required');
        return;
      }

      const uploaded: MessageAttachment[] = [];
      for (const file of stagedFiles) {
        const formData = new FormData();
        formData.append('file', file);
        const uploadResponse = await fetch(
          `${BACKEND_URL}/support-tickets/${ticketId}/attachments`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          }
        );
        if (!uploadResponse.ok) {
          throw new Error(`Attachment upload failed: ${uploadResponse.status}`);
        }
        const meta = await uploadResponse.json();
        uploaded.push({
          storagePath: meta.storagePath,
          fileName: meta.fileName,
          contentType: meta.contentType,
          size: meta.size,
        });
      }

      await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: newMessage,
          ...(uploaded.length > 0 ? { attachments: uploaded } : {}),
        }),
      });
      if (draftMeta) {
        fireDraftEvent('sent', {
          editDistanceRatio: editDistanceRatio(deliveredDraftText, newMessage),
        });
        setDraftMeta(null);
        setDraftEdited(false);
        setDraftEnglish('');
        setDraftState('idle');
      }
      setNewMessage('');
      setStagedFiles([]);
    } catch (err) {
      setError('An error occurred while sending the message.');
    } finally {
      setIsSending(false);
    }
  };

  const handleAttachmentDownload = async (storagePath: string) => {
    try {
      const token = await user?.getIdToken();
      if (!token) {
        setError('Authentication required');
        return;
      }
      const response = await fetch(
        `${BACKEND_URL}/support-tickets/${ticketId}/attachments/signed-url?storage_path=${encodeURIComponent(storagePath)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) {
        throw new Error(`Signed URL fetch failed: ${response.status}`);
      }
      const data = await response.json();
      window.open(data.downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setError('Could not fetch the attachment download link.');
    }
  };

  const requestDraft = async () => {
    if (draftState === 'generating') return;
    // Snapshot whatever the agent typed — restored on failure or discard.
    // (Regenerate keeps the ORIGINAL pre-draft snapshot, so only snapshot from idle/error.)
    const snapshot = draftState === 'present' ? draftSnapshot : newMessage;
    setDraftSnapshot(snapshot);
    setDraftState('generating');
    try {
      const token = await user?.getIdToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      const response = await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/draft-reply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Draft request failed: ${response.status}`);
      }
      const data = await response.json();
      setNewMessage(data.text);
      setDeliveredDraftText(data.text);
      // The draft is written in the customer's language. Agents were being
      // asked to approve text they could not read.
      setDraftEnglish(typeof data.englishText === 'string' ? data.englishText : '');
      setIsEnglishOpen(true);
      setDraftMeta({
        draftId: data.draftId,
        language: data.language,
        grounded: data.grounded,
        sources: data.sources ?? [],
      });
      setDraftEdited(false);
      setDraftState('present');
    } catch (err) {
      setNewMessage(snapshot);
      setDraftState('error');
    }
  };

  const discardDraft = () => {
    fireDraftEvent('discarded');
    setNewMessage(draftSnapshot);
    setDraftMeta(null);
    setDraftEdited(false);
    setDraftEnglish('');
    setDraftState('idle');
    draftButtonRef.current?.focus();
  };

  // Normalized Levenshtein distance (two-row DP); inputs capped for safety.
  const editDistanceRatio = (a: string, b: string): number => {
    const s = a.slice(0, 4000);
    const t = b.slice(0, 4000);
    if (s === t) return 0;
    if (!s.length || !t.length) return 1;
    let prev = Array.from({ length: t.length + 1 }, (_, i) => i);
    for (let i = 1; i <= s.length; i += 1) {
      const curr = [i];
      for (let j = 1; j <= t.length; j += 1) {
        curr[j] = Math.min(
          prev[j] + 1,
          curr[j - 1] + 1,
          prev[j - 1] + (s[i - 1] === t[j - 1] ? 0 : 1)
        );
      }
      prev = curr;
    }
    return Math.round((prev[t.length] / Math.max(s.length, t.length)) * 100) / 100;
  };

  const LANGUAGE_LABELS: Record<string, string> = {
    en: 'English',
    de: 'German',
    pt: 'Portuguese',
    uk: 'Ukrainian',
    fr: 'French',
  };

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    const valid = picked.filter(
      (f) =>
        ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type) &&
        f.size <= 20 * 1024 * 1024
    );
    if (valid.length < picked.length) {
      setError('Only PDF, JPG, or PNG files up to 20 MB are allowed.');
    }
    setStagedFiles((prev) => [...prev, ...valid]);
    e.target.value = '';
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    submitMessage();
  };

  const handleStatusChange = async (status: string) => {
    if (!user || !ticketId) return;

    try {
      const token = await user.getIdToken();
      await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
    } catch (err) {
      setError('An error occurred while closing the ticket.');
    }
  };

  const updateAssignee = async (assignee: string) => {
    if (!user || !ticketId || assignmentBusy) return;

    setAssignmentBusy(true);
    try {
      const token = await user.getIdToken();
      await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/assignee`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assignee }),
      });
    } catch (err) {
      setError('An error occurred while updating the assignee.');
    } finally {
      setAssignmentBusy(false);
    }
  };

  const startEditing = (message: Message) => {
    setEditingMessageId(message.id);
    setEditDraft(message.text);
    setEditError('');
  };

  const cancelEditing = () => {
    setEditingMessageId(null);
    setEditDraft('');
    setEditError('');
  };

  const saveEdit = async (message: Message) => {
    if (!user || !ticketId || editBusy) return;
    const text = editDraft.trim();
    if (!text && !(message.attachments && message.attachments.length > 0)) {
      setEditError('A reply cannot be empty.');
      return;
    }
    if (text === message.text.trim()) {
      cancelEditing();
      return;
    }

    setEditBusy(true);
    setEditError('');
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/support-tickets/${ticketId}/messages/${message.id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ text }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        setEditError(data?.detail || 'Could not save the edit.');
        return;
      }
      // The backend dropped the cached translation of the old wording; let
      // the translate effect ask for a fresh one once the snapshot shows it gone.
      translationRequestedFor.current.delete(message.id);
      cancelEditing();
    } catch (err) {
      setEditError('Could not save the edit.');
    } finally {
      setEditBusy(false);
    }
  };

  // Fetched rather than subscribed to (they live where the client SDK cannot
  // read them), so a colleague's reaction shows up on the next refresh: on
  // load, after any toggle, and every 30 seconds while the ticket is open.
  const loadReactions = useCallback(async () => {
    if (!user || !ticketId) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BACKEND_URL}/support-tickets/${ticketId}/reactions`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      const next: Record<string, MessageReactions> = {};
      Object.entries((data?.reactions ?? {}) as Record<string, unknown>).forEach(([messageId, value]) => {
        const parsed = normalizeReactions(value);
        if (parsed) next[messageId] = parsed;
      });
      setReactionsByMessage(next);
    } catch {
      // Reactions are a nicety; the thread works without them.
    }
  }, [user, ticketId, BACKEND_URL]);

  useEffect(() => {
    setReactionsByMessage({});
    loadReactions();
    const timer = window.setInterval(loadReactions, 30000);
    return () => window.clearInterval(timer);
  }, [loadReactions]);

  const toggleReaction = async (messageId: string, reaction: string) => {
    if (!user || !ticketId) return;
    setReactionPickerFor(null);
    if (reactionBusy.current.has(messageId)) return;
    reactionBusy.current.add(messageId);
    setReactionError(null);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/support-tickets/${ticketId}/messages/${messageId}/reactions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reaction }),
        }
      );
      if (!response.ok) {
        setReactionError({ messageId, text: 'Could not update the reaction.' });
        return;
      }
      const data = await response.json().catch(() => null);
      const updated = normalizeReactions(data?.reactions) ?? {};
      setReactionsByMessage((previous) => ({ ...previous, [messageId]: updated }));
    } catch (err) {
      setReactionError({ messageId, text: 'Could not update the reaction.' });
    } finally {
      reactionBusy.current.delete(messageId);
    }
  };

  // "Take it" self-assigns to the signed-in agent; the backend validates the
  // email against the lawyers collection (which every agent already belongs to).
  const takeTicket = () => updateAssignee(user?.email ?? '');
  const releaseTicket = () => updateAssignee('');

  const statusHint =
    ticket?.status === 'waiting_on_user' && ticket.statusChangedBy === 'agent_reply'
      ? AUTO_WAITING_HINT
      : STATUS_OPTIONS.find((option) => option.value === ticket?.status)?.hint ?? '';

  const assignedToMe =
    Boolean(ticket?.assignedTo) &&
    Boolean(user?.email) &&
    ticket!.assignedTo.toLowerCase() === user!.email!.toLowerCase();

  if (isLoading) return <p>Loading ticket...</p>;
  if (error) return <p className="error-message">❌ {error}</p>;
  if (!ticket) return null;

  return (
    <div className="ticket-detail-page" data-cy="agent-case-detail">
      <div className="ticket-header">
        <div className="ticket-header-info">
          {/* /support/ticket/:id was a dead end: the only way back to the queue
              was the browser's own back button. */}
          <Link to="/support" className="ticket-back-link" data-cy="back-to-inbox">
            ← Support Center
          </Link>
          <h1>{ticket.subject}</h1>
          <div className="ticket-header-meta">
            <span>{ticket.userName}</span>
            {ticket.category && (
              <span className="ticket-header-pill">{ticket.category.replace(/_/g, ' ')}</span>
            )}
            {ticket.relatedCaseId && (
              <span title={ticket.relatedCaseId}>
                Related case #{ticket.relatedCaseId.substring(0, 8)}
              </span>
            )}
            {ticket.description && (
              <span className="ticket-header-desc" title={ticket.description}>
                {ticket.description}
              </span>
            )}
          </div>
        </div>
        <div className="ticket-actions">
            <button
              type="button"
              className="customer-profile-trigger"
              onClick={() => setIsProfileOpen(true)}
              data-cy="open-customer-profile"
            >
              Customer profile{customerCases.length ? ` (${customerCases.length})` : ''}
            </button>
            {canCreateMarketplaceRequest && (
              <button
                type="button"
                className="customer-profile-trigger"
                onClick={() => setIsMarketplaceDialogOpen(true)}
                data-cy="create-marketplace-request"
              >
                Create marketplace request
              </button>
            )}
            <div className="ticket-assignment" data-cy="agent-assignment">
              {ticket.assignedTo ? (
                <>
                  <span className="ticket-assignment-label">
                    {assignedToMe
                      ? 'Assigned to you'
                      : `Assigned to ${ticket.assignedToName || ticket.assignedTo}`}
                  </span>
                  {assignedToMe ? (
                    <button
                      type="button"
                      className="ticket-assignment-btn ticket-assignment-release"
                      onClick={releaseTicket}
                      disabled={assignmentBusy}
                      data-cy="agent-release-ticket"
                    >
                      Release
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ticket-assignment-btn"
                      onClick={takeTicket}
                      disabled={assignmentBusy}
                      data-cy="agent-take-ticket"
                    >
                      Take over
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="ticket-assignment-btn"
                  onClick={takeTicket}
                  disabled={assignmentBusy}
                  data-cy="agent-take-ticket"
                >
                  Take it
                </button>
              )}
              {supportUsers.length > 0 && (
                <label className="ticket-assignment-picker">
                  <span className="visually-hidden">Assign to</span>
                  <select
                    value={ticket.assignedTo || ''}
                    onChange={(e) => updateAssignee(e.target.value)}
                    disabled={assignmentBusy}
                    data-cy="agent-assignee-select"
                    title="Assign this case to someone. They get an email."
                  >
                    <option value="">Unassigned</option>
                    {supportUsers.map((candidate) => (
                      <option key={candidate.email} value={candidate.email}>
                        {candidate.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <label className="ticket-action-field">
              <span>Status: </span>
              <select
                value={ticket.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                data-cy="agent-status-select"
                title={statusHint}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {statusHint && (
              <p className="ticket-status-hint" data-cy="status-hint">
                {statusHint}
              </p>
            )}
        </div>
      </div>
      {isMarketplaceDialogOpen && (
        <CreateMarketplaceRequestDialog
          ticketId={ticket.id}
          userId={ticket.userId}
          customerName={ticket.userName === DEFAULT_CUSTOMER_LABEL ? '' : ticket.userName}
          onClose={() => setIsMarketplaceDialogOpen(false)}
        />
      )}
      {isProfileOpen && (
        <div
          className="customer-profile-overlay"
          onClick={() => setIsProfileOpen(false)}
          role="presentation"
        >
          <aside
            className="customer-profile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Customer profile"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="customer-profile-header">
              <div>
                <h2 className="customer-profile-name">{ticket.userName}</h2>
                {ticket.userId && (
                  <p className="customer-profile-id" title={ticket.userId}>
                    ID: {ticket.userId}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="customer-profile-close"
                onClick={() => setIsProfileOpen(false)}
                aria-label="Close customer profile"
              >
                ×
              </button>
            </div>
            <h3 className="customer-cases-title">
              Cases{customerCases.length ? ` (${customerCases.length})` : ''}
            </h3>
            {casesLoading ? (
              <p className="customer-cases-empty">Loading cases…</p>
            ) : customerCases.length ? (
              <ul className="customer-cases-list">
                {customerCases.map((customerCase) => {
                  const status =
                    customerCase.lawyer_status || customerCase.status || 'Unknown';
                  return (
                    <li key={customerCase.id} className="customer-case-item">
                      <Link to={`/case/${customerCase.id}`} className="customer-case-service">
                        {customerCase.service || 'Case'}
                      </Link>
                      <span
                        className={`customer-case-status status-${status
                          .toString()
                          .toLowerCase()
                          .replace(/[\s_]+/g, '-')}`}
                      >
                        {status}
                      </span>
                      {/* Both dates are labelled. The panel used to show a bare
                          created_at next to the status, and agents read it as
                          the completion date and told customers so. */}
                      <span className="customer-case-dates">
                        <span className="customer-case-date" data-cy="case-started">
                          <span className="customer-case-date-label">Started</span>
                          {formatTimestamp(customerCase.created_at) || '—'}
                        </span>
                        <span className="customer-case-date" data-cy="case-completed">
                          <span className="customer-case-date-label">Completed</span>
                          {formatTimestamp(customerCase.completed_at) || 'not yet'}
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="customer-cases-empty">No cases found for this customer.</p>
            )}
          </aside>
        </div>
      )}
      <div className="message-list">
        {messages.map((message) => {
          const isAgentMessage =
            Boolean(user?.uid) && message.senderUid === user?.uid;
          const senderName = isAgentMessage
            ? user?.displayName || 'You'
            : message.senderName || ticket.userName || DEFAULT_CUSTOMER_LABEL;
          const cacheKey = getAvatarKey(message);
          const avatarState = avatarCache[cacheKey];
          const fallbackInitial =
            avatarState?.fallbackInitial ?? getInitialFromLabel(senderName);
          const timestampLabel = formatTimestamp(message.timestamp);
          const avatarAlt = `${senderName} avatar`;
          const translation = translations[message.id];

          return (
            <div
              key={message.id}
              className={`message-item ${isAgentMessage ? 'sent' : 'received'}`}
            >
              <AvatarBubble
                cacheKey={cacheKey}
                avatarState={avatarState}
                fallbackInitial={fallbackInitial}
                alt={avatarAlt}
                onLoad={handleAvatarLoad}
                onError={handleAvatarError}
              />
              <div className="message-content">
                <div className="message-sender">{senderName}</div>
                {editingMessageId === message.id ? (
                  <div className="message-edit" data-cy="message-edit">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') cancelEditing();
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          saveEdit(message);
                        }
                      }}
                      rows={Math.min(12, Math.max(3, editDraft.split('\n').length + 1))}
                      aria-label="Edit reply"
                      autoFocus
                    />
                    <p className="message-edit-note">
                      The customer already has the original in their email. This changes the thread only.
                    </p>
                    {editError && <p className="message-edit-error">{editError}</p>}
                    <div className="message-edit-actions">
                      <button type="button" onClick={cancelEditing} disabled={editBusy}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="message-edit-save"
                        onClick={() => saveEdit(message)}
                        disabled={editBusy}
                        data-cy="message-edit-save"
                      >
                        {editBusy ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p>{message.text}</p>
                )}
                {translation && translation.sourceLanguage !== 'en' && (
                  <div className="message-translation" data-cy="message-translation">
                    <span className="message-translation-label">
                      Translated from {languageLabel(translation.sourceLanguage)}
                    </span>
                    <p>{translation.text}</p>
                  </div>
                )}
                {message.attachments && message.attachments.length > 0 && (
                  <div className="message-attachments">
                    {message.attachments.map((attachment) => (
                      <button
                        key={attachment.storagePath}
                        type="button"
                        className="attachment-chip"
                        onClick={() => handleAttachmentDownload(attachment.storagePath)}
                        title={`Download ${attachment.fileName}`}
                      >
                        📎 {attachment.fileName}
                        {formatAttachmentSize(attachment.size) && (
                          <span className="attachment-size"> ({formatAttachmentSize(attachment.size)})</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <span className="message-timestamp">
                  {timestampLabel || '—'}
                  {message.editedAt && (
                    <span
                      className="message-edited"
                      data-cy="message-edited"
                      title={`Edited ${formatTimestamp(message.editedAt)}`}
                    >
                      {' '}· edited
                    </span>
                  )}
                </span>
                <div className="message-actions">
                  {REACTIONS.filter((r) => reactionsByMessage[message.id]?.[r.key]?.length).map((r) => {
                    const entries = reactionsByMessage[message.id][r.key];
                    const mine = entries.some((entry) => entry.uid === user?.uid);
                    return (
                      <button
                        key={r.key}
                        type="button"
                        className={mine ? 'reaction-chip reaction-chip--mine' : 'reaction-chip'}
                        onClick={() => toggleReaction(message.id, r.key)}
                        title={entries.map((entry) => entry.name).join(', ')}
                        aria-label={`${r.label}: ${entries.length}${mine ? ', including you' : ''}`}
                        aria-pressed={mine}
                        data-cy={`reaction-${r.key}`}
                      >
                        <span aria-hidden="true">{r.emoji}</span> {entries.length}
                      </button>
                    );
                  })}
                  <span className="reaction-picker-anchor">
                    <button
                      type="button"
                      className="message-action"
                      onClick={() =>
                        setReactionPickerFor(reactionPickerFor === message.id ? null : message.id)
                      }
                      aria-expanded={reactionPickerFor === message.id}
                      aria-label="Add reaction"
                      title="Add reaction (only the team sees it)"
                      data-cy="reaction-add"
                    >
                      ☺+
                    </button>
                    {reactionPickerFor === message.id && (
                      <span className="reaction-picker" role="menu" data-cy="reaction-picker">
                        {REACTIONS.map((r) => (
                          <button
                            key={r.key}
                            type="button"
                            role="menuitem"
                            onClick={() => toggleReaction(message.id, r.key)}
                            title={r.label}
                            aria-label={r.label}
                            data-cy={`reaction-pick-${r.key}`}
                          >
                            {r.emoji}
                          </button>
                        ))}
                      </span>
                    )}
                  </span>
                  {/* Replies from before senderRole existed carry none; the
                      backend accepts those from their author too. */}
                  {isAgentMessage &&
                    message.senderRole !== 'user' &&
                    editingMessageId !== message.id && (
                      <button
                        type="button"
                        className="message-action"
                        onClick={() => startEditing(message)}
                        data-cy="message-edit-button"
                      >
                        Edit
                      </button>
                    )}
                </div>
                {reactionError?.messageId === message.id && (
                  <p className="message-action-error" role="alert" data-cy="reaction-error">
                    {reactionError.text}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="reply-composer">
      <div className="composer-tabs" role="tablist" aria-label="Composer">
        <button
          type="button"
          role="tab"
          aria-selected={composerTab === 'reply'}
          className={composerTab === 'reply' ? 'composer-tab active' : 'composer-tab'}
          onClick={() => setComposerTab('reply')}
          data-cy="composer-tab-reply"
        >
          Reply to customer
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={composerTab === 'note'}
          className={composerTab === 'note' ? 'composer-tab active' : 'composer-tab'}
          onClick={() => setComposerTab('note')}
          data-cy="composer-tab-note"
        >
          Internal note{notes.length ? ` (${notes.length})` : ''}
        </button>
        {/* A long draft fills the pane and pushes the customer's message off
            the top, exactly when an agent wants to re-read what they are
            answering. Minimize parks the draft without losing a word of it. */}
        <button
          type="button"
          className="composer-minimize"
          onClick={() => setIsComposerCollapsed((collapsed) => !collapsed)}
          aria-expanded={!isComposerCollapsed}
          data-cy="composer-minimize"
          title={
            isComposerCollapsed
              ? 'Expand the composer'
              : 'Minimize the composer to read the conversation'
          }
        >
          {isComposerCollapsed ? '▴ Expand' : '▾ Minimize'}
        </button>
      </div>
      {composerTab === 'note' ? (
        <div className="notes-panel" data-cy="notes-panel">
          <p className="notes-privacy" data-cy="notes-privacy">
            🔒 Internal only. The customer never sees notes.
          </p>
          {notes.length > 0 && (
            <ul className="notes-list">
              {notes.map((note) => (
                <li key={note.id} className="note-item" data-cy="note-item">
                  <div className="note-meta">
                    <strong>{note.authorName || note.authorEmail || 'Support'}</strong>
                    <span>{formatTimestamp(note.createdAt) || ''}</span>
                  </div>
                  <p className="note-text">{note.text}</p>
                </li>
              ))}
            </ul>
          )}
          <div className="note-composer">
            <textarea
              ref={noteRef}
              className={isComposerCollapsed ? 'composer-collapsed' : undefined}
              value={noteText}
              onChange={handleNoteChange}
              onKeyDown={handleNoteKeyDown}
              placeholder="Write a note for the team. Type @ to mention someone."
              data-cy="note-input"
              disabled={noteSending}
            />
            {mentionQuery !== null && mentionMatches.length > 0 && (
              <ul className="mention-menu" data-cy="mention-menu" role="listbox">
                {mentionMatches.map((candidate, index) => (
                  <li key={candidate.email}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === mentionIndex}
                      className={index === mentionIndex ? 'mention-option active' : 'mention-option'}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        insertMention(candidate);
                      }}
                      data-cy="mention-option"
                    >
                      <strong>{candidate.name}</strong>
                      <span>{candidate.email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="note-save-btn"
              onClick={submitNote}
              disabled={noteSending || !noteText.trim()}
              data-cy="note-save"
            >
              {noteSending ? 'Saving…' : 'Save note'}
            </button>
          </div>
          {noteError && (
            <p className="error-message" data-cy="note-error" role="alert">
              {noteError}
            </p>
          )}
        </div>
      ) : (
      <>
      {draftState === 'error' && (
        <div className="draft-error" data-cy="draft-error" role="alert">
          Couldn't generate a draft right now.{' '}
          <button type="button" className="draft-error-retry" onClick={requestDraft}>
            Try again
          </button>
        </div>
      )}
      {draftState === 'present' && (
        <div className="draft-review-banner" data-cy="draft-review-banner" role="status" aria-live="polite">
          <span className="draft-review-label" id="draft-review-label">
            {draftEdited ? 'AI draft — edited' : 'AI draft — review and edit before sending'}
          </span>
          <button
            type="button"
            className="draft-banner-action"
            onClick={() => {
              fireDraftEvent('regenerated');
              void requestDraft();
            }}
          >
            Regenerate
          </button>
          <button type="button" className="draft-banner-action" onClick={discardDraft}>
            Discard
          </button>
        </div>
      )}
      {draftState === 'present' && draftEnglish && (
        <div className="draft-english" data-cy="draft-english">
          <button
            type="button"
            className="draft-english-toggle"
            onClick={() => setIsEnglishOpen((open) => !open)}
            aria-expanded={isEnglishOpen}
          >
            {isEnglishOpen ? '▾' : '▸'} What this says in English
            {draftMeta ? ` (drafted in ${LANGUAGE_LABELS[draftMeta.language] ?? draftMeta.language})` : ''}
          </button>
          {isEnglishOpen && (
            <p className="draft-english-text" data-cy="draft-english-text">
              {draftEnglish}
            </p>
          )}
          {draftEdited && (
            <p className="draft-english-stale" data-cy="draft-english-stale">
              You have edited the draft since this translation.
            </p>
          )}
        </div>
      )}
      <form className="message-form" onSubmit={handleSendMessage}>
        <textarea
          ref={replyRef}
          className={isComposerCollapsed ? 'composer-collapsed' : undefined}
          value={newMessage}
          onChange={e => {
            setNewMessage(e.target.value);
            if (draftState === 'error') setDraftState('idle');
            if (draftState === 'present' && !draftEdited) setDraftEdited(true);
          }}
          placeholder="Type your reply..."
          data-cy="agent-reply-input"
          disabled={draftState === 'generating'}
          aria-describedby={draftState === 'present' ? 'draft-review-label' : undefined}
        />
        {ticket.status !== 'closed' && (
          <button
            type="button"
            ref={draftButtonRef}
            className="draft-reply-btn"
            data-cy="draft-reply-btn"
            aria-label="Draft a reply with AI"
            aria-busy={draftState === 'generating'}
            disabled={draftState === 'generating' || isSending}
            onClick={requestDraft}
          >
            {draftState === 'generating' ? '✨ Drafting…' : '✨ Draft reply'}
          </button>
        )}
        <label className="attach-label">
          <input
            type="file"
            data-cy="agent-reply-attach"
            multiple
            accept="application/pdf,image/jpeg,image/png"
            onChange={handleFilePick}
            style={{ display: 'none' }}
            disabled={draftState === 'generating'}
          />
          📎 Attach
        </label>
        <button type="submit" disabled={isSending || draftState === 'generating'} data-cy="agent-reply-send">Send</button>
      </form>
      {draftState === 'present' && draftMeta && (
        <div className="draft-grounding" data-cy="draft-grounding" role="group" aria-label="Draft sources">
          {draftMeta.grounded ? (
            <>
              {draftMeta.sources.map((source) => (
                <span
                  key={`${source.type}-${source.title}`}
                  className="draft-grounding-chip"
                  data-cy="draft-grounding-chip"
                  title={source.title}
                >
                  {source.type === 'thread'
                    ? `this thread (${source.messageCount ?? '?'} messages)`
                    : source.title}
                </span>
              ))}
              <span className="draft-grounding-chip" data-cy="draft-grounding-chip">
                reply in: {LANGUAGE_LABELS[draftMeta.language] ?? draftMeta.language}
              </span>
            </>
          ) : (
            <span className="draft-grounding-chip draft-chip-warning" data-cy="draft-grounding-chip">
              not grounded in KB — verify manually
            </span>
          )}
        </div>
      )}
      {stagedFiles.length > 0 && (
        <div className="staged-attachments">
          {stagedFiles.map((file, index) => (
            <span key={`${file.name}-${index}`} className="attachment-chip">
              📎 {file.name}
              <button
                type="button"
                className="attachment-remove"
                aria-label={`Remove ${file.name}`}
                onClick={() =>
                  setStagedFiles((prev) => prev.filter((_, i) => i !== index))
                }
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      </>
      )}
      </div>
    </div>
  );
};

export default SupportTicketDetailPage;
export { AvatarBubble, decodeHtmlEntities };
